<template>
    <div :class="[
        'shrink-0 sticky top-0 z-50 bg-white w-full h-[64px] border-b border-gray-DFDFDF',
        'px-[20px] lg:px-[40px] flex items-center justify-between font-oceanside',
    ]">
        <reset-panel-button
            class="w-[18px]"
        />
        <h1
            class="flex-1 text-center text-base -mr-[16px] pointer-events-none flex flex-col font-semibold"
        >
            {{ $t('INITIALS') }}
        </h1>
        <close-button
            width="18.84"
            @click="handleClose"
        />
    </div>

    <placement-header-alt
        v-if="!isMobile"
        :placement="mainPlacement"
        :placementData="placementData"
        :hide-combine="true"
    />

    <mobile-product-renderer v-if="isMobile" />

    <div class="sticky top-0 z-10 -mt-[1px]">
        <step-options-color-controls
            v-if="isAnyPaintfillSelected"
            :is-active="true"
        />
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto pb-[20px] font-oceanside px-[20px] lg:px-[40px] relative">
        <step-options-characters-initials-buttons
            class="mt-[24px]"
            :main-placement="mainPlacement"
            :option="option"
            :all-components="allComponents"
            :has-add-remove-button="true"
            :use-palette-color-selection="usePaletteColorSelection"
            :remove-selected-on-add-remove-click="true"
            :has-image="!isMobile"
            :navigate-on-click="false"
            @unselect="handleRemoveSelection"
            background-size="auto 100%"
        />
        <div v-if="hasText1 || hasText2">
            <initials-input
                v-if="hasText1"
                :limit="textLimit"
                :characters="textCharacters.replace(/|||/g, '')"
                :placement="mainPlacement"
                attribute="v-stamping-text-1"
                :should-separate-with-periods="separateWithPeriods"
                :is-dirty="effectiveIsDirty"
                @handleReset="$emit('reset')"
                @update-value="handleUpdateValue"
                @validation-error="text1ValidationError = $event"
            />

            <initials-input
                v-if="hasText2"
                :limit="textLimit"
                :characters="textCharacters.replace(/|||/g, '')"
                :placement="mainPlacement"
                attribute="v-stamping-text-2"
                :should-separate-with-periods="separateWithPeriods"
                :is-dirty="effectiveIsDirty"
                @update-value="handleUpdateValue"
                @validation-error="text2ValidationError = $event"
            />

            <div class="py-2">
                <plus-minus-checkbox
                    :checkbox-id="`separate-with-periods-${option?.value}`"
                    :value="separateWithPeriods"
                    @change="toggleSeparateWithPeriods"
                    :useCheckForMinus="true"
                    shape="square"
                >
                    {{$t(textLimit == 4 ? 'SEPARATE_WITH_PERIODS_4_CHARACTERS' : 'SEPARATE_WITH_PERIODS')}}
                </plus-minus-checkbox>
            </div>

            <characters-list
                v-if="allowedCharacters"
                :characters="allowedCharacters"
            />

            <div class="py-2 pt-4">
                <div class="flex justify-between mb-[16px]">
                    <span class="text-[16px] font-semibold font-oceanside text-ink">{{ $t('LETTER_STYLE') }}</span>
                    <span class="text-[16px] font-oceanside text-ink text-right">{{
                        letterStyles.find((x) => x.value === selectedLetterStyle)?.label
                    }}</span>
                </div>
                <div class="flex flex-wrap gap-2.5">
                    <button
                        v-for="letterStyle in letterStyles"
                        :key="letterStyle.value"
                        :class="[
                            'w-[148px] h-[86px] rounded-sm border ',
                            { 'text-white border-black': letterStyle.value === selectedLetterStyle },
                            { 'border-gray-light': letterStyle.value !== selectedLetterStyle },
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                        ]"
                        @click="selectedLetterStyle = letterStyle.value"
                        :style="{
                            backgroundImage: `url(&quot;${getImage(letterStyle)}&quot;)`,
                            backgroundSize: 'cover',
                        }"
                    >
                        <img
                            :src="letterStyle.image"
                            :alt="letterStyle.label"
                            class="w-full h-full object-cover object-center"
                            height="24"
                        >
                    </button>
                </div>
            </div>

            <initials-colors
                v-if="hasText1 && !usePaletteColorSelection"
                :placement="mainPlacement"
                :title="$t(resolvedText1Caption)"
                :colors-placement="'StampingColorsLine1'"
                :has-error-state="hasInvalidText1Color"
                @color-selected="handleTextColorSelected"
            />

            <initials-colors
                v-if="hasText2 && !usePaletteColorSelection"
                :placement="mainPlacement"
                :title="$t(hasText1 ? 'SECOND_LINE_COLORS' : 'COLORS')"
                :colors-placement="'StampingColorsLine2'"
                :has-error-state="hasInvalidText2Color"
                @color-selected="handleTextColorSelected"
            />

            <step-options-front-initials-list
                :has-error-state="hasInvalidText1Color || hasInvalidText2Color"
                @color-selected="handleTextColorSelected"
            />
        </div>
    </div>
    <step-options-apply-selections
        class="shrink-0"
        @apply-selections="handleClose"
        @handle-disabled="handleDisabled"
        :is-disabled="isDisabled"
        :is-dirty="effectiveIsDirty"
        :should-close-group-overlay="false"
        :should-show-initials-requirement="shouldShowInitialsRequirement"
        :button-text="$t('APPLY_INITIALS')"
    />
</template>

<script>
    import CharactersList from './CharactersList.vue';
    import InitialsInput from './InitialsInput.vue';
    import InitialsColors from './InitialsColors.vue';
    import PlusMinusCheckbox from './PlusMinusCheckbox.vue';
    import allStores from '../stores/all';
    import getImage from '../helpers/getimage';
    import StepOptionsFrontInitialsList from './StepOptionsFrontInitialsList.vue';
    import CloseButton from './buttons/CloseButton.vue';
    import ResetPanelButton from './buttons/ResetPanelButton.vue';
    import StepOptionsApplySelections from './StepOptionsApplySelections.vue';
    import MobileProductRenderer from './MobileProductRenderer.vue';
    import StepOptionsCharactersInitialsButtons from './buttons/StepOptionsCharactersInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import StepOptionsColorControls from './StepOptionsColorControls.vue';

    export default {
        name: 'StepOptionsCharactersInitialsOptions',

        mixins: [allStores],

        components: {
            CharactersList,
            InitialsInput,
            InitialsColors,
            PlusMinusCheckbox,
            StepOptionsFrontInitialsList,
            CloseButton,
            ResetPanelButton,
            StepOptionsApplySelections,
            MobileProductRenderer,
            StepOptionsCharactersInitialsButtons,
            PlacementHeaderAlt,
            StepOptionsColorControls,
        },

        props: {
            allComponents: {
                type: Array,
                required: true,
            },

            isDirty: {
                type: Boolean,
                default: false,
            },

            mainPlacement: {
                type: Object,
                required: true,
            },

            option: {
                type: Object,
                default: null,
            },

            usePaletteColorSelection: {
                type: Boolean,
                default: false,
            },
        },

        emits: ['reset'],

        data() {
            return {
                separateWithPeriods: false,
                selectedLetterStyle: 'straight',
                text1ValidationError: false,
                text2ValidationError: false,
            };
        },

        computed: {
            placementData() {
                const characterStampingGroup = this.customizerStore.visibleGroups
                    ?.find((g) => g.name === 'Character Stamping');

                return {
                    name: 'Character Stamping',
                    code: 'CharacterStamping',
                    placements: [
                        ...((characterStampingGroup?.placements) || []),
                    ],
                };
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            isAnyPaintfillSelected() {
                return this.customizerStore.isAnyPaintfillSelected();
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            shouldShowInitialsRequirement() {
                return this.effectiveIsDirty && !this.isComplete;
            },

            enableFocusLock() {
                return this.appStore.activeNestedPanel?.panelId === 'charactersInitialsOptions';
            },

            effectiveIsDirty() {
                return this.isDirty || this.hasAttemptedApply;
            },

            hasAttemptedApply() {
                return this.customizerStore.hasFrontNestedPanelValidationAttempted('initials');
            },

            currentComponent() {
                return this.viewController.selectedAtPlacement(this.mainPlacement.code);
            },

            hasText1() {
                return this.currentComponent?.custom?.['v-stamping-has-text-1'] === 'yes';
            },

            hasText2() {
                return this.currentComponent?.custom?.['v-stamping-has-text-2'] === 'yes';
            },

            resolvedText1Caption() {
                if (this.hasText2) {
                    return 'FIRST_LINE_COLORS';
                }

                return 'COLORS';
            },

            frontInitialsOptionByName() {
                return (this.customizerStore.uiStepSubOptions?.frontInitials || [])
                    .reduce((optionsByName, option) => ({
                        ...optionsByName,
                        [option.name]: option,
                    }), {});
            },

            hasInvalidText1Color() {
                return this.customizerStore.panelsInErrorState.includes('Front-StampingColorsLine1');
            },

            hasInvalidText2Color() {
                return this.customizerStore.panelsInErrorState.includes('Front-StampingColorsLine2');
            },

            hasSelectedText1Color() {
                const frontInitialsOption = this.frontInitialsOptionByName.StampingColorsLine1;

                if (frontInitialsOption?.selectedColor) {
                    return true;
                }

                const selectedColor = this.viewController.selectedAtPlacement('StampingColorsLine1');

                return !!(selectedColor && !selectedColor.placeholder);
            },

            hasSelectedText2Color() {
                const frontInitialsOption = this.frontInitialsOptionByName.StampingColorsLine2;

                if (frontInitialsOption?.selectedColor) {
                    return true;
                }

                const selectedColor = this.viewController.selectedAtPlacement('StampingColorsLine2');

                return !!(selectedColor && !selectedColor.placeholder);
            },

            isDisabled() {
                return this.inErrorState || !this.isComplete;
            },

            isComplete() {
                const vc = this.customizerStore.viewController;
                const initials = vc?.selectedAtPlacement('StampingStyle');
                const hasInitials = !!(initials && !initials.placeholder);
                const hasTextValidationError = (this.hasText1 && this.text1ValidationError)
                    || (this.hasText2 && this.text2ValidationError);

                if (hasInitials && hasTextValidationError) {
                    return false;
                }

                if (hasInitials) {
                    const requiresText1 = initials?.custom?.['v-stamping-has-text-1'] === 'yes';
                    const text1Value = initials?.custom?.['v-stamping-text-1'];
                    const requiresText2 = initials?.custom?.['v-stamping-has-text-2'] === 'yes';
                    const text2Value = initials?.custom?.['v-stamping-text-2'];
                    const hasRequiredTextColors = (!requiresText1 || this.hasSelectedText1Color)
                        && (!requiresText2 || this.hasSelectedText2Color);

                    if (!hasRequiredTextColors) {
                        return false;
                    }

                    const text1Ok = !requiresText1 || (text1Value != null && `${text1Value}`.length > 0);
                    const text2Ok = !requiresText2 || (text2Value != null && `${text2Value}`.length > 0);

                    return text1Ok && text2Ok;
                }

                return false;
            },

            hasSymbols() {
                return this.currentComponent?.custom?.['v-stamping-has-symbols'] === 'yes';
            },

            hasAddons() {
                return !!this.currentComponent?.custom?.['v-stamping-supports-snow-symbol'];
            },

            hasOptions() {
                return this.hasText1 || this.hasText2 || this.hasSymbols || this.hasAddons;
            },

            textCharacters() {
                return this.currentComponent?.custom['v-stamping-characters'];
            },

            allowedCharacters() {
                const characters = this.textCharacters;

                if (characters) {
                    return characters.split('|||').map((line) => line.split(''));
                }

                return null;
            },

            textLimit() {
                const agsCode = this.currentComponent.custom?.['v-ags-code'];

                if (agsCode) {
                    const limit = this.mainPlacement.custom?.[`v-${agsCode}-limit`];

                    if (limit != null) {
                        return +limit;
                    }
                }

                if (this.viewController.blueprint?.custom?.['v-stamping-limit'] != null) {
                    return this.viewController.blueprint?.custom?.['v-stamping-limit'];
                }

                const ownLimit = this.currentComponent?.custom?.['v-stamping-limit'];

                return +ownLimit;
            },

            groupPreviewImage() {
                return this.mainPlacement?.custom['v-group-preview'];
            },

            allLetterStyles() {
                const styles = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const component of this.mainPlacement.components) {
                    if (component.placeholder) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (!this.viewController.isAvailable(component)) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const code = component.code;
                    const separateWithPeriods = this.getDotsStyle(code);
                    const selectedLetterStyle = this.getDancingStyle(code);
                    const image = getImage(this.viewController, component);

                    styles.push({
                        selected: this.currentComponent?.code === code,
                        separateWithPeriods,
                        selectedLetterStyle,
                        code,
                        image,
                        label: this.viewController.componentNames(component).join(' '),
                    });
                }

                return styles;
            },

            letterStyles() {
                const styles = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const style of this.allLetterStyles) {
                    if (style.separateWithPeriods) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (styles.some((x) => x.value === style.selectedLetterStyle)) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    styles.push({
                        value: style.selectedLetterStyle,
                        image: style.image,
                        label: style.label,
                    });
                }

                return styles?.reverse() || [];
            },
        },

        watch: {
            selectedLetterStyle() {
                this.handleUpdateLetterStyle();
            },

            separateWithPeriods() {
                this.handleUpdateLetterStyle();
            },

            isComplete(value) {
                if (value) {
                    this.customizerStore.clearFrontNestedPanelValidationAttempted('initials');
                    this.clearTextColorErrors();
                }
            },

            'customizerStore.uiStepSubOptions.frontInitials': {
                deep: true,
                handler() {
                    if (this.effectiveIsDirty) {
                        this.updateTextColorErrors();
                    }
                },
            },
        },

        mounted() {
            this.setFromComponent();

            if (this.getPreferredView() !== 'Face') {
                this.selectInitialsView();
            }
        },

        unmounted() {
            this.customizerStore.removePanelFromErrorState('StampSymbols');
        },

        methods: {
            textColorState(colorsPlacement) {
                if (colorsPlacement === 'StampingColorsLine1') {
                    return {
                        errorStateKey: 'Front-StampingColorsLine1',
                        hasSelectedColor: this.hasSelectedText1Color,
                    };
                }

                if (colorsPlacement === 'StampingColorsLine2') {
                    return {
                        errorStateKey: 'Front-StampingColorsLine2',
                        hasSelectedColor: this.hasSelectedText2Color,
                    };
                }

                return null;
            },

            updateTextColorError(colorsPlacement, isRequired) {
                const state = this.textColorState(colorsPlacement);

                if (!state) {
                    return;
                }

                if (!isRequired || state.hasSelectedColor) {
                    this.customizerStore.removePanelFromErrorState(state.errorStateKey);
                    return;
                }

                this.customizerStore.addPanelToErrorState(state.errorStateKey);
            },

            updateTextColorErrors() {
                this.updateTextColorError('StampingColorsLine1', this.hasText1);
                this.updateTextColorError('StampingColorsLine2', this.hasText2);
            },

            clearTextColorErrors() {
                this.customizerStore.removePanelFromErrorState('Front-StampingColorsLine1');
                this.customizerStore.removePanelFromErrorState('Front-StampingColorsLine2');
            },

            handleTextColorSelected(colorsPlacement) {
                const state = this.textColorState(colorsPlacement);

                if (state?.hasSelectedColor) {
                    this.customizerStore.removePanelFromErrorState(state.errorStateKey);
                }
            },

            getDancingStyle(code) {
                return this.customizerStore.getDancingStyle(code);
            },

            getDotsStyle(code) {
                return this.customizerStore.getDotsStyle(code);
            },

            setFromComponent() {
                const currentComponent = this.viewController.selectedAtPlacement(this.mainPlacement.code);

                if (currentComponent) {
                    const code = currentComponent.code;

                    this.separateWithPeriods = this.getDotsStyle(code);
                    this.selectedLetterStyle = this.getDancingStyle(code);
                }
            },

            handleUpdateLetterStyle() {
                let targetCode = null;

                if (this.separateWithPeriods) {
                    if (this.selectedLetterStyle === 'dancing') {
                        targetCode = 'CP011:CPDND';
                    }

                    if (this.selectedLetterStyle === 'straight') {
                        targetCode = 'CP011:CPSTD';
                    }
                } else {
                    if (this.selectedLetterStyle === 'dancing') {
                        targetCode = 'CP011:CPDN';
                    }

                    if (this.selectedLetterStyle === 'straight') {
                        targetCode = 'CP011:CPST';
                    }
                }

                const matchingLetterStyle = this.allComponents.find((x) => x.code.endsWith(targetCode));
                const currentComponent = this.viewController.selectedAtPlacement(this.mainPlacement.code);
                const preservedAttributes = {};

                if (currentComponent?.custom?.['v-stamping-has-text-1'] === 'yes') {
                    const text = currentComponent.custom['v-stamping-text-1'] || '';

                    preservedAttributes['v-stamping-text-1'] = text;
                }

                if (currentComponent?.custom?.['v-stamping-has-text-2'] === 'yes') {
                    const text = currentComponent.custom['v-stamping-text-2'] || '';

                    preservedAttributes['v-stamping-text-2'] = text;
                }

                this.viewController.updateComponent(this.mainPlacement.code, matchingLetterStyle.code);

                if (Object.keys(preservedAttributes).length > 0) {
                    this.viewController.commitCustomAttributes(this.mainPlacement.code, preservedAttributes);
                }
            },

            handleUpdateValue() {
                this.customizerStore.stateChanged(false);
                this.selectInitialsView();
            },

            handleDisabled() {
                this.customizerStore.setFrontNestedPanelValidationAttempted('initials', true);
                this.updateTextColorErrors();
            },

            async handleRemoveSelection() {
                await this.customizerStore.resetCharacterStamping();
                this.text1ValidationError = false;
                this.text2ValidationError = false;
                this.clearTextColorErrors();
                this.appStore.closeNestedPanel();
                this.selectInitialsView();
            },

            getImage(option) {
                if (option == null) {
                    return '';
                }

                if (this.groupPreviewImage) {
                    return this.groupPreviewImage;
                }

                return getImage(this.viewController, option.raw);
            },

            toggleSeparateWithPeriods({ target }) {
                this.separateWithPeriods = target.checked;
                this.selectInitialsView();
            },

            handleClose() {
                this.appStore.closeNestedPanel();
                this.selectInitialsView();
            },

            getPreferredView() {
                let useView = 'Face';

                const component = this.viewController.selectedAtPlacement('PutterParent');
                if (component) {
                    const joint = component.joints.find((x) => x.code === 'Personalization');

                    if (joint) {
                        const faceSettings = joint.views?.Face;
                        if (faceSettings?.hidden === 'true') {
                            // Switch to sole instead.
                            useView = 'Sole';
                        }
                    }
                }

                return useView;
            },

            selectInitialsView() {
                const useView = this.getPreferredView();

                this.customizerStore.setActiveView(useView);
            },
        },
    };
</script>

<style scoped>
#cz3 .flex-wrap {
    flex-wrap: wrap !important;
}
</style>
